import { gql } from '@apollo/client';
import { camelCase, snakeCase } from 'lodash';
import { dateFormFormat } from '@/components/utils/formatter';
import { Schema } from './schema';

export const REGULAR_HEALTH_CHECKUP_REPORT = gql`
  query regularHealthCheckupReport($id: ID!) {
    client {
      id
      regularHealthCheckupReport(id: $id) {
        id
        office {
          id
        }
        officeSnapshot {
          id
          name
          postcode0
          postcode1
          prefecture {
            id
            name
          }
          city
          houseNumber
          building
          phoneNumber0
          phoneNumber1
          phoneNumber2
          laborInsuranceNumber0
          laborInsuranceNumber1
          laborInsuranceNumber2
          laborInsuranceNumber3
          laborInsuranceNumber4
          repPositionName
          repLastName
          repFirstName
          laborStandardsInspectionOfficeLShortName
        }
        businessType {
          id
          name
        }
        title
        startOn
        endOn
        reportNumber
        healthCheckupInstitution {
          id
          name
          location
        }
        healthCheckupInstitutionsCount
        physicianName
        physicianInstitutionName
        physicianInstitutionAddress
        submitOn
        aggregationOffices {
          id
          name
        }
        resultSummaries {
          id
          itemCode
          totalCount
          hasFindingCount
        }
        specifiedWorkSummaries {
          id
          specifiedWorkCode
          count
        }
        enrolledEmployeesCount
        checkedEmployeesCount
      }
      offices {
        id
        name
        socialContactInfo {
          ...LmsOfficeContactInfo
        }
        laborContactInfo {
          ...LmsOfficeContactInfo
        }
        ownerContactInfo {
          ...LmsOfficeContactInfo
        }
        laborInsuranceNumber0
        laborInsuranceNumber1
        laborInsuranceNumber2
        laborInsuranceNumber3
        laborInsuranceNumber4
        laborStandardsInspectionOfficeL {
          id
          shortName
        }
      }
    }
    businessTypes {
      id
      name
    }
    prefectures {
      id
      name
    }
  }

  fragment LmsOfficeContactInfo on LmsOfficeContactInfo {
    id
    name
    postcode0
    postcode1
    prefecture {
      id
    }
    city
    houseNumber
    building
    phoneNumber0
    phoneNumber1
    phoneNumber2
    repPositionName
    repLastName
    repFirstName
  }
`;

export interface RegularHealthCheckupReportQueryVariables {
  id: string;
}

export interface RegularHealthCheckupReportQueryResult {
  client: {
    regularHealthCheckupReport?: RegularHealthCheckupReport;
    offices: Office[];
  };
  businessTypes: BusinessType[];
  prefectures: Prefecture[];
}

export interface RegularHealthCheckupReport {
  id: string;
  office?: {
    id: string;
  };
  officeSnapshot?: {
    name?: string;
    postcode0?: string;
    postcode1?: string;
    prefecture?: {
      id: string;
      name: string;
    };
    city?: string;
    houseNumber?: string;
    building?: string;
    phoneNumber0?: string;
    phoneNumber1?: string;
    phoneNumber2?: string;
    laborInsuranceNumber0?: string;
    laborInsuranceNumber1?: string;
    laborInsuranceNumber2?: string;
    laborInsuranceNumber3?: string;
    laborInsuranceNumber4?: string;
    repPositionName?: string;
    repLastName?: string;
    repFirstName?: string;
    laborStandardsInspectionOfficeLShortName?: string;
  };
  businessType?: {
    id: string;
    name: string;
  };
  title: string;
  startOn?: string;
  endOn?: string;
  reportNumber?: number;
  healthCheckupInstitution?: {
    id: string;
    name: string;
    location: string;
  };
  healthCheckupInstitutionsCount: number;
  physicianName?: string;
  physicianInstitutionName?: string;
  physicianInstitutionAddress?: string;
  submitOn?: string;
  aggregationOffices: {
    id: string;
    name: string;
  }[];
  resultSummaries: {
    id: string;
    itemCode: string;
    totalCount: number;
    hasFindingCount: number;
  }[];
  specifiedWorkSummaries: {
    id: string;
    specifiedWorkCode: string;
    count: number;
  }[];
  enrolledEmployeesCount?: number;
  checkedEmployeesCount?: number;
}

export interface Office {
  id: string;
  name: string;
  socialContactInfo?: LmsOfficeContactInfo;
  laborContactInfo?: LmsOfficeContactInfo;
  ownerContactInfo?: LmsOfficeContactInfo;
  laborInsuranceNumber0?: string;
  laborInsuranceNumber1?: string;
  laborInsuranceNumber2?: string;
  laborInsuranceNumber3?: string;
  laborInsuranceNumber4?: string;
  laborStandardsInspectionOfficeL?: LmsGovernmentOffice;
}

export interface LmsOfficeContactInfo {
  id: string;
  name?: string;
  postcode0?: string;
  postcode1?: string;
  prefecture?: {
    id: string;
  };
  city?: string;
  houseNumber?: string;
  building?: string;
  phoneNumber0?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
  repPositionName?: string;
  repLastName?: string;
  repFirstName?: string;
}

export interface LmsGovernmentOffice {
  id: string;
  shortName: string;
}

export interface BusinessType {
  id: string;
  name: string;
}

export interface Prefecture {
  id: string;
  name: string;
}

export const generateDefaultValues = (data: RegularHealthCheckupReport): Schema => {
  const values: Schema = {
    title: data.title,
    officeId: data.office?.id,
    officeSnapshot: data.officeSnapshot
      ? {
          name: data.officeSnapshot.name,
          postcode0: data.officeSnapshot.postcode0,
          postcode1: data.officeSnapshot.postcode1,
          prefectureId: data.officeSnapshot.prefecture?.id,
          city: data.officeSnapshot.city,
          houseNumber: data.officeSnapshot.houseNumber,
          building: data.officeSnapshot.building,
          phoneNumber0: data.officeSnapshot.phoneNumber0,
          phoneNumber1: data.officeSnapshot.phoneNumber1,
          phoneNumber2: data.officeSnapshot.phoneNumber2,
          laborInsuranceNumber0: data.officeSnapshot.laborInsuranceNumber0,
          laborInsuranceNumber1: data.officeSnapshot.laborInsuranceNumber1,
          laborInsuranceNumber2: data.officeSnapshot.laborInsuranceNumber2,
          laborInsuranceNumber3: data.officeSnapshot.laborInsuranceNumber3,
          laborInsuranceNumber4: data.officeSnapshot.laborInsuranceNumber4,
          repPositionName: data.officeSnapshot.repPositionName,
          repLastName: data.officeSnapshot.repLastName,
          repFirstName: data.officeSnapshot.repFirstName,
          laborStandardsInspectionOfficeLShortName: data.officeSnapshot.laborStandardsInspectionOfficeLShortName
        }
      : {},
    businessTypeId: data.businessType?.id,
    startOn: dateFormFormat(data.startOn, 'L'),
    endOn: dateFormFormat(data.endOn, 'L'),
    reportNumber: `${data.reportNumber ?? ''}`,
    aggregationOfficeIds: data.aggregationOffices.map(office => office.id),
    healthCheckupInstitutionName: data.healthCheckupInstitution?.name,
    healthCheckupInstitutionLocation: data.healthCheckupInstitution?.location,
    healthCheckupInstitutionsCount: data.healthCheckupInstitutionsCount,
    specifiedWorkSummaries: {},
    resultSummaries: {},
    physicianName: data.physicianName,
    physicianInstitutionName: data.physicianInstitutionName,
    physicianInstitutionAddress: data.physicianInstitutionAddress,
    submitOn: dateFormFormat(data.submitOn, 'L'),
    enrolledEmployeesCount: `${data.enrolledEmployeesCount ?? ''}`,
    checkedEmployeesCount: `${data.checkedEmployeesCount ?? ''}`
  };

  for (const specifiedWorkSummary of data.specifiedWorkSummaries) {
    values.specifiedWorkSummaries[camelCase(specifiedWorkSummary.specifiedWorkCode)] = {
      count: `${specifiedWorkSummary.count}`
    };
  }

  for (const resultSummary of data.resultSummaries) {
    values.resultSummaries[camelCase(resultSummary.itemCode)] = {
      totalCount: `${resultSummary.totalCount}`,
      hasFindingCount: `${resultSummary.hasFindingCount}`
    };
  }

  return values;
};

export const UPDATE_REGULAR_HEALTH_CHECKUP_REPORT = gql`
  mutation updateRegularHealthCheckupReport($input: UpdateRegularHealthCheckupReportInput!) {
    updateRegularHealthCheckupReport(input: $input) {
      regularHealthCheckupReport {
        id
      }
    }
  }
`;

export interface UpdateRegularHealthCheckupReportQueryVariables {
  input: {
    id: string;
    attributes: {
      title: string;
      officeId?: string;
      officeSnapshot?: {
        name?: string;
        postcode0?: string;
        postcode1?: string;
        prefectureId?: string;
        city?: string;
        houseNumber?: string;
        building?: string;
        phoneNumber0?: string;
        phoneNumber1?: string;
        phoneNumber2?: string;
        laborInsuranceNumber0?: string;
        laborInsuranceNumber1?: string;
        laborInsuranceNumber2?: string;
        laborInsuranceNumber3?: string;
        laborInsuranceNumber4?: string;
        repPositionName?: string;
        repLastName?: string;
        repFirstName?: string;
        laborStandardsInspectionOfficeLShortName?: string;
      };
      businessTypeId?: string;
      startOn?: string;
      endOn?: string;
      reportNumber?: number;
      aggregationOfficeIds?: string[];
      specifiedWorkSummaries?: {
        specifiedWorkCode: string;
        count: number;
      }[];
      resultSummaries?: {
        itemCode: string;
        totalCount: number;
        hasFindingCount: number;
      }[];
      physicianName?: string | null;
      physicianInstitutionName?: string | null;
      physicianInstitutionAddress?: string | null;
      submitOn?: string;
      enrolledEmployeesCount?: number;
      checkedEmployeesCount?: number;
    };
  };
}

export interface UpdateRegularHealthCheckupReportQueryResult {
  updateRegularHealthCheckupReport: {
    regularHealthCheckupReport: {
      id: string;
    };
  };
}

export const convertFormDataToQueryVariables = (
  data: Schema,
  id: string
): UpdateRegularHealthCheckupReportQueryVariables => {
  return {
    input: {
      id,
      attributes: {
        title: data.title,
        officeId: data.officeId,
        officeSnapshot: data.officeSnapshot,
        businessTypeId: data.businessTypeId,
        startOn: data.startOn,
        endOn: data.endOn,
        reportNumber: data.reportNumber ? parseInt(data.reportNumber, 10) : undefined,
        aggregationOfficeIds: data.aggregationOfficeIds,
        specifiedWorkSummaries: Object.entries(data.specifiedWorkSummaries).map(([specifiedWorkCode, item]) => ({
          specifiedWorkCode: snakeCase(specifiedWorkCode),
          count: item?.count ? parseInt(item.count, 10) : 0
        })),
        resultSummaries: Object.entries(data.resultSummaries).map(([itemCode, item]) => ({
          itemCode: snakeCase(itemCode).replace('_hz', 'hz'),
          totalCount: item?.totalCount ? parseInt(item.totalCount, 10) : 0,
          hasFindingCount: item?.hasFindingCount ? parseInt(item.hasFindingCount, 10) : 0
        })),
        physicianName: data.physicianName,
        physicianInstitutionName: data.physicianInstitutionName,
        physicianInstitutionAddress: data.physicianInstitutionAddress,
        submitOn: data.submitOn,
        enrolledEmployeesCount: data.enrolledEmployeesCount ? parseInt(data.enrolledEmployeesCount, 10) : undefined,
        checkedEmployeesCount: data.checkedEmployeesCount ? parseInt(data.checkedEmployeesCount, 10) : undefined
      }
    }
  };
};
